import React from 'react';
import Head from 'next/head';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { loadArticle } from 'redux/modules/article';
import { loadShowLead, loadPostNavigation } from 'redux/modules/showBlog';
import { bulkUpdate as navUpdateAction } from 'redux/modules/navbar';
import { setPageView } from 'redux/modules/shared';

import BTE from 'lib/BTE';
import { NEWSLETTER_SIGNUP_POPUP_MODULE } from 'lib/brandFeatures';
import { ArticleContext } from 'lib/ContextTypes';
import {
  article as ArticlePropType,
  package as packagePropType,
  showBlogNavigation as showBlogNavigationPropType,
} from 'lib/CustomPropTypes';
import { layout as servicesLayoutPropType } from 'lib/CustomPropTypes/services';
import { getFeatureConfigForBrand } from 'lib/getFeatureStatus';
import { getVideosFromArticlePayload } from 'lib/getVideosFromPayload';
import { getPathName } from 'lib/getPathName';
import LegacyIDService from 'lib/LegacyIDService';
import { navbar, NAVBAR_THEME } from 'lib/navbar';
import { setLinkHeaders } from 'lib/setLinkHeaders';

import AdsBundle from 'components/AdsBundle';
import { ThemesCssBundle } from 'components/ThemesCssBundle';
import { AnalyticsLaunchScripts } from 'components/AnalyticsLaunchScripts';
import ShowBlogHero from 'components/ShowBlog/ShowBlogHero';
import { ShowBlogBody } from 'components/ShowBlog/ShowBlogBody';
import { ErrorBoundary } from 'components/ErrorBoundary';
import IntObserver from 'components/IntObserver';
import PopupModule from 'components/NewsletterSignup/PopupModule';
import { ArticleMetadata, GlobalMetadata } from 'components/PageMetadata';
import { HeaderAndFooter } from 'components/services/HeaderAndFooter';
import ScrollingAnalytics from 'components/ScrollingAnalytics';
import IconfontStyleTag from 'components/IconfontStyleTag';
import UniversalCheckout from 'components/UniversalCheckout';
import ShowLead from 'components/packages/ShowLead';
import { ShowBlogNavigation } from 'components/ShowBlog/Navigation';

import ErrorPage from '../_error';

import 'assets/styles/main.scss';
import 'assets/styles/toolkit.scss';
import globalContainerStyles from '../globalContainerStyles.module.scss';
import './styles.themed.scss';

const block = 'showBlogPage';
const pageView = 'showBlog';
const externalLink = 'externalLink';

const navbarConfig = {
  /**
   * @returns {string}
   */
  theme() {
    return NAVBAR_THEME.TRANSPARENT;
  },
  /**
   * @param {object} [root0]
   * @param {string} [root0.content]
   */
  headline: ({ content }) => content?.headline ?? null,
  activeAt: 1,
  setDuration: 0,
  /**
   * @returns {boolean}
   */
  showShortcuts: () => true,
};

/**
 * @param {object} root0
 * @param {object} root0.article
 * @param {object} root0.shared
 * @param {object} root0.router
 * @param {object} root0.navbar
 */
const mapStateToProps = ({
  article, shared, router, navbar: navStore,
}) => ({
  articles: article.content,
  vertical: shared.vertical,
  isChromeless: shared.isChromeless,
  path: router.path,
  active: navStore.active,
});

/**
 * @param {Function} dispatch
 */
const mapActionsToProps = (dispatch) => ({
  /**
   * @param {string} theme
   */
  navUpdate: (theme) => dispatch(navUpdateAction(theme)),
});

class ShowBlogPage extends React.Component {
  /**
   * @param {object} ctx
   * @param {object} ctx.store
   * @param {object} ctx.req
   * @param {object} ctx.req.params
   * @param {string} ctx.req.params.id
   */
  static getInitialProps = async (ctx) => {
    const { store } = ctx;
    const {
      params: { id: rawID },
    } = ctx.req;
    const {
      locals: {
        vertical,
        showBlogConfig,
      },
    } = ctx.res;

    const id = LegacyIDService.article(rawID);

    const promises = [
      store.dispatch(loadArticle(id)),
      store.dispatch(setPageView(pageView)),
    ];

    await Promise.all(promises);

    const articleStateAfterLoad = store.getState().article;
    const articleSubType = articleStateAfterLoad?.content?.[0]?.subType;
    const primaryURL = articleStateAfterLoad?.content?.[0]?.url?.primary ?? '';
    const headline = articleStateAfterLoad?.content?.[0]?.headline ?? {};

    if (articleSubType === externalLink) {
      ctx.res.redirect(primaryURL);
    }

    const statusCode = articleStateAfterLoad?.error?.status ?? 200;

    const nextPromises = [];

    if (showBlogConfig?.frontRoute) {
      nextPromises.push(store.dispatch(loadShowLead(vertical, showBlogConfig.frontRoute)));

      const path = articleStateAfterLoad?.content?.[0]?.taxonomy?.primaryTopic?.path;
      const createdAt = articleStateAfterLoad?.content?.[0]?.date?.createdAt;
      const excludedId = articleStateAfterLoad?.content?.[0]?.id;
      nextPromises.push(store.dispatch(loadPostNavigation(path, createdAt, excludedId)));
    }

    nextPromises.push(store.dispatch(navUpdateAction({
      shareUrl: getPathName(primaryURL),
      headline,
    })));

    await Promise.all(nextPromises);

    setLinkHeaders(ctx.res, vertical);

    const showBlogStateAfterLoad = store.getState().showBlog;
    const showLead = showBlogStateAfterLoad?.showLead ?? null;
    const showBlogNav = showBlogStateAfterLoad?.nav ?? null;
    const hideTopNavigation = showBlogConfig?.pageNavigation?.hideTop ?? false;

    if (showLead?.metadata) {
      if (!showLead.metadata.navigation) {
        showLead.metadata.navigation = [];
      }
      showLead.metadata.navigation.unshift({
        linkTitle: showBlogConfig.allPostsText || 'ALL POSTS',
        linkUrl: showBlogConfig.allPostsLink,
      });
    }

    return {
      navigating: false,
      pageView,
      showBlogNav,
      showLead,
      statusCode,
      hideTopNavigation,
    };
  };

  static propTypes = {
    articles: PropTypes.arrayOf(ArticlePropType).isRequired,
    isChromeless: PropTypes.bool,
    layout: servicesLayoutPropType.isRequired,
    path: PropTypes.string,
    showLead: packagePropType.isRequired,
    showBlogNav: showBlogNavigationPropType.isRequired,
    statusCode: PropTypes.number.isRequired,
    vertical: PropTypes.string.isRequired,
    hideTopNavigation: PropTypes.bool,
  };

  static defaultProps = {
    isChromeless: false,
    path: '',
    hideTopNavigation: false,
  };

  static childContextTypes = {
    isShowBlog: PropTypes.bool,
  };

  /**
   * @returns {{ isShowBlog: boolean }}
   */
  getChildContext = () => ({
    isShowBlog: true,
  });

  /** */
  handleMainContentError = () => <ErrorPage statusCode={500} />;

  /**
   * @param {object[]} [e]
   */
  onView = (e) => {
    if (e?.[0]?.isIntersecting) {
      // eslint-disable-next-line react/destructuring-assignment
      const id = this.props?.articles?.[0]?.id;
      BTE.trigger('articleInView', id);
    }
  };

  /**
   * @returns {void}
   */
  render() {
    const {
      articles,
      isChromeless,
      layout,
      path,
      showLead,
      showBlogNav,
      statusCode,
      vertical,
      hideTopNavigation,
    } = this.props;

    if (parseInt(statusCode, 10) >= 400) {
      return (
        <ErrorPage
          statusCode={statusCode === 404 ? 404 : 500}
          vertical={vertical}
          layout={layout}
        />
      );
    }

    const article = articles?.length && articles[0];
    const { id: articleID } = article;

    const shouldNewsletterSignupPopupModuleRender = getFeatureConfigForBrand(
      NEWSLETTER_SIGNUP_POPUP_MODULE,
      vertical,
    );

    const gridBodyClasses = classNames(
      'layout-grid-item',
      'grid-col-10-m grid-col-push-1-m',
      'grid-col-7-l grid-col-push-0-l',
      'grid-col-6-xl grid-col-push-1-xl',
      'showblog-body--custom-column',
    );

    // 100000% top margin to pretend the viewport is "infinitely" tall, so you will
    // still be considered "past" the top of the article
    // -100% bottom margin to get the 0 threshold to trigger only once the top of the article
    // passes the top of the viewport
    const intObsRootMargin = '100000% 0px -100% 0px';

    const allVideos = getVideosFromArticlePayload(article);

    const content = (
      <ErrorBoundary errorHandler={this.handleMainContentError}>
        <IntObserver
          rootMargin={intObsRootMargin}
          threshold={0}
          callback={this.onView}
        >
          <ScrollingAnalytics contentType="article" contentUrl={article.url.primary}>
            <ArticleContext.Provider value={article}>
              <GlobalMetadata />
              <ArticleMetadata
                article={article}
                vertical={vertical}
                currentPath={path}
                allVideos={allVideos}
                isShowBlog
              />
              <div key={`artcleWrapper-${article.id}`} className={block}>
                {shouldNewsletterSignupPopupModuleRender && <PopupModule />}

                {showLead && (
                  <ShowLead
                    packageId={showLead.id}
                    content={{ context: {}, ...showLead }}
                    pkgClassName={classNames('pkg lead showLead', `${block}__showLead`)}
                  />
                )}

                {(showBlogNav && !hideTopNavigation) && (
                  <ShowBlogNavigation
                    currentArticleId={articleID}
                    previousArticle={showBlogNav.previous}
                    nextArticle={showBlogNav.next}
                    latestArticle={showBlogNav.latest}
                    hideLatestOverride
                    additionalClasses="showblog-navigation-top layout-grid-container layout-grid-container-s"
                  />
                )}
                <ShowBlogHero
                  content={article}
                  vertical={vertical}
                  headlineClassName={[
                    'mb3 mt0',
                    `${block}__headline`,
                  ]}
                  isChromeless={isChromeless}
                  gridBodyClasses={gridBodyClasses}
                />
                <ShowBlogBody
                  vertical={vertical}
                  article={article}
                  index={0}
                  path={path}
                  isChromeless={isChromeless}
                  showBlogNav={showBlogNav}
                  gridBodyClasses={gridBodyClasses}
                />
              </div>
            </ArticleContext.Provider>
          </ScrollingAnalytics>
        </IntObserver>
      </ErrorBoundary>
    );

    const wrappedContent = isChromeless ? (
      content
    ) : (
      <HeaderAndFooter layout={layout}>{content}</HeaderAndFooter>
    );

    return (
      <>
        <Head>
          <IconfontStyleTag />
        </Head>
        <ThemesCssBundle vertical={vertical} />

        <div
          className={classNames(
            globalContainerStyles.container,
            'bg-knockout-primary',
          )}
          id="content"
        >
          {wrappedContent}
        </div>
        <AnalyticsLaunchScripts isChromeless={isChromeless} pageID={articleID} />
        <AdsBundle />
        <UniversalCheckout vertical={vertical} />
      </>
    );
  }
}

export default navbar(navbarConfig)(
  connect(mapStateToProps, mapActionsToProps)(ShowBlogPage),
);

// exported for testing
export { ShowBlogPage as UnwrappedShowBlogPage };
